import type { WritableSignal } from '@angular/core';

import type { BadgeColor } from '@core/user-interface-legacy';
import { WalkthroughStepId } from '@core/walkthrough-types';

import type { TellentAppRouteID, TellentPageConfig } from './routes';
import { AppKind } from './types';

export type TellentNavigationScope = 'core_hr' | 'grow' | 'admin' | 'recruitment';

export type TellentNavigationPage = TellentPageConfig<TellentAppRouteID>;

export type NavigationItemType = 'separator' | 'group' | 'link' | 'section';

export type TellentNavigationAppKind = AppKind | `${AppKind}` | 'client';

export type TellentNavigationItemBadge = {
  text: string;
  color: BadgeColor;
};

export type TellentNavigationItemUpgrade = {
  label: string;
  page: TellentNavigationPage;
  path: string;
};

export type TellentNavigationItemContext = {
  isActive?: boolean;
  isChild?: boolean;
  isExpanded?: boolean;
  type: NavigationItemType;
  appKind: TellentNavigationAppKind;
};

export type TellentGetIconParams = {
  appKind: string;
  id: string;
  icon: string | null;
  iconSvg: string | null;
  isChild: boolean;
  isActive: boolean;
};

export type TellentNavigationIcon =
  | {
      icon: string;
      iconSvg: null;
    }
  | {
      icon: null;
      iconSvg: string;
    };

export type TellentNavigationLink = {
  label: string;
  appKind: TellentNavigationAppKind;
  type: 'link';
  badges?: TellentNavigationItemBadge[];
  page: TellentNavigationPage;
  path: string;
  testId: string;
  walkthroughId?: WalkthroughStepId;
  upgrade?: TellentNavigationItemUpgrade;
  availableOnMobile: boolean;
  handler: TellentRouteCustomHandler<any> | null;
} & TellentNavigationIcon &
  TellentNavigationItemContext;

export type TellentNavigationSeparator = {
  label: string;
  type: 'separator';
  availableOnMobile: boolean;
} & TellentNavigationItemContext;

export type TellentNavigationGroup = {
  label: string;
  type: 'group';
  id: string;
  children: TellentNavigationItem[];
  badges?: TellentNavigationItemBadge[];
} & TellentNavigationIcon &
  TellentNavigationItemContext;

export type TellentNavigationSection = {
  label: string;
  appKind: TellentNavigationAppKind;
  type: 'section';
  children: TellentNavigationItem[];
} & TellentNavigationItemContext;

export type TellentNavigationItem =
  | TellentNavigationGroup
  | TellentNavigationLink
  | TellentNavigationSeparator
  | TellentNavigationSection;

export type TellentRouteCustomHandler<T> = {
  trigger: (location: string, cleanup: (previousUrl: string) => void) => T;
  dispose: (ref: T) => void;
};

export type TellentRoute = {
  id: string;
  testId: string;
  walkthroughId?: WalkthroughStepId;
  buildPath: (params?: unknown) => string;
  matchPath: (location: string, path: string) => boolean;
  matchPattern: (location: string) => boolean;
  handler: TellentRouteCustomHandler<any> | null;
};

export const TellentClientHomepageID = 'default';
export const TellentClientSettingsHomepageID = 'settings:default';

export type TellentRouteHomepage = TellentRoute & { id: typeof TellentClientHomepageID };
export type TellentRouteSettingsHomepage = TellentRoute & {
  id: typeof TellentClientSettingsHomepageID;
};

export type TellentRouteFactoryOptions = Partial<{
  matchPath?: TellentRoute['matchPath'];
  testId?: string;
  walkthroughId?: WalkthroughStepId;
  handler?: TellentRouteCustomHandler<any>;
}>;

export type TellentRouteFactoryFn = (
  id: string,
  urlStrOrFactory: string | ((args?: Record<string, string | number>) => string),
  options?: TellentRouteFactoryOptions,
) => TellentRoute;

export type TellentHomeRouteFactoryFn = (
  path: string,
  options?: TellentRouteFactoryOptions,
) => TellentRoute & { id: typeof TellentClientHomepageID };

export type TellentSettingsHomeRouteFactoryFn = (
  path: string,
  options?: TellentRouteFactoryOptions,
) => TellentRoute & { id: typeof TellentClientSettingsHomepageID };

export enum TellentNavigationViewID {
  Main = 'main',
  Settings = 'settings',
}

export type TellentNavigationLinkLocalState = {
  target: Pick<TellentNavigationLink, 'page'>;
  patch: Partial<TellentNavigationLink>;
};

export type TellentNavigationHttpContract = {
  id: TellentNavigationViewID;
  sections: TellentNavigationItem[];
  linksLocalState: Record<string, TellentNavigationLinkLocalState>;
};

export interface TellentNavigationControllerI {
  recognizedRoutes: WritableSignal<TellentRoute[]>;
  minimized: WritableSignal<boolean>;
  location: WritableSignal<string>;
  handleNavigationLinkClick: (url: string) => void;
}

export type TellentRedirectObject = {
  params: Record<string, string | number>;
  id: string;
};

export type TellentNavigationViewModel = {
  active: TellentNavigationLink | null;
  children: TellentNavigationItem[];
  hasMultipleSections: boolean;
};
